import React from 'react';
import type { AdminEmployee } from '../../types/models';
import type { ParttimePayroll } from '../PayrollTypes';
import { minutesToDecimalHours, minutesToHoursMinutes } from '../PayrollTypes';
import {
  calculatePartSpecialAllowance,
  getBenefitLabels,
  PART_SPECIAL_ALLOWANCE_LABEL,
  resolveParttimeBenefitUnitPrices,
} from '../PayrollBenefitRules';
import PayrollOverrideCell from './PayrollOverrideCell';

interface ParttimeBenefitTableProps {
  employees: AdminEmployee[];
  payrollData: ParttimePayroll[];
  autoValues: Map<string, Map<string, number>>;
  overrides: Map<string, Map<string, number>>;
  onOverrideChange: (employeeId: string, fieldName: string, value: number | null) => void;
  benefitRuleEnabled?: boolean;
  partSpecialAllowanceEnabled?: boolean;
}

export default function ParttimeBenefitTable({
  employees, payrollData, autoValues, overrides, onOverrideChange, benefitRuleEnabled = false,
  partSpecialAllowanceEnabled = false,
}: ParttimeBenefitTableProps) {
  const findEmployee = (id: string) => employees.find(e => e.id === id);
  const getAutoValue = (employeeId: string, fieldName: string): number =>
    autoValues.get(employeeId)?.get(fieldName) ?? 0;
  const isOverridden = (employeeId: string, fieldName: string): boolean =>
    overrides.get(employeeId)?.has(fieldName) ?? false;

  if (payrollData.length === 0) {
    return <p className="text-gray-500 text-sm py-4 text-center">{'\u8A72\u5F53\u3059\u308B\u975E\u5E38\u52E4\u304C\u3044\u307E\u305B\u3093'}</p>;
  }

  if (partSpecialAllowanceEnabled) {
    return (
      <div>
        <p className="mb-2 text-xs text-gray-500">
          {`${PART_SPECIAL_ALLOWANCE_LABEL} = 勤務時間の合計（29分以下は切り捨て、30分以上は切り上げ）× 時間単価`}
        </p>
        <div className="max-h-[calc(100dvh-20rem)] overflow-auto overscroll-contain">
        <table className="w-full text-sm border-collapse">
          <thead className="sticky top-0 z-20 bg-gray-50 shadow-sm">
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="px-3 py-2 text-left font-medium text-gray-700 sticky left-0 bg-gray-50 z-10">{'氏名'}</th>
              <th className="px-3 py-2 text-right font-medium text-gray-700">{'勤務時間'}</th>
              <th className="px-3 py-2 text-right font-medium text-gray-700">端数処理後の時間</th>
              <th className="px-3 py-2 text-right font-medium text-gray-700">時間単価</th>
              <th className="px-3 py-2 text-right font-medium text-gray-700">{`${PART_SPECIAL_ALLOWANCE_LABEL}(計算)`}</th>
              <th className="px-3 py-2 text-right font-medium text-gray-700 bg-amber-50/50">{`${PART_SPECIAL_ALLOWANCE_LABEL}(支給)`}</th>
            </tr>
          </thead>
          <tbody>
            {payrollData.map(p => {
              const emp = findEmployee(p.employeeId);
              if (!emp) return null;
              const { hours, unitPrice, amount } = calculatePartSpecialAllowance(emp, p.totalWorkMinutes);

              return (
                <tr key={p.employeeId} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-3 py-2 whitespace-nowrap sticky left-0 bg-white z-10">{p.employeeName}</td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">{minutesToHoursMinutes(p.totalWorkMinutes)}</td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">{`${hours}時間`}</td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    {unitPrice === null ? (
                      <span
                        className="whitespace-nowrap rounded bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800"
                        title="従業員管理でパート特別手当の時間単価を設定してください"
                      >
                        未設定
                      </span>
                    ) : unitPrice.toLocaleString()}
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">{amount.toLocaleString()}</td>
                  <td className="px-1 py-1">
                    <PayrollOverrideCell
                      value={p.specialAllowance}
                      autoValue={getAutoValue(p.employeeId, 'special_allowance')}
                      isOverridden={isOverridden(p.employeeId, 'special_allowance')}
                      format="currency"
                      onChange={(v) => onOverrideChange(p.employeeId, 'special_allowance', v)}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>
      </div>
    );
  }

  const labels = getBenefitLabels(benefitRuleEnabled);

  return (
    <div className="max-h-[calc(100dvh-18rem)] overflow-auto overscroll-contain">
      <table className="w-full text-sm border-collapse">
        <thead className="sticky top-0 z-20 bg-gray-50 shadow-sm">
          <tr className="bg-gray-50 border-b border-gray-200">
            <th className="px-3 py-2 text-left font-medium text-gray-700 sticky left-0 bg-gray-50 z-10">{'\u6C0F\u540D'}</th>
            <th className="px-3 py-2 text-right font-medium text-gray-700">{'\u52E4\u52D9\u6642\u9593'}</th>
            {benefitRuleEnabled ? (
              <>
                <th className="px-3 py-2 text-right font-medium text-gray-700">{`${labels.allowanceLabel}\u5358\u4FA1`}</th>
                <th className="px-3 py-2 text-right font-medium text-gray-700">{`${labels.allowanceLabel}(\u8A08\u7B97)`}</th>
                <th className="px-3 py-2 text-right font-medium text-gray-700 bg-amber-50/50">{`${labels.allowanceLabel}(\u652F\u7D66)`}</th>
                <th className="px-3 py-2 text-right font-medium text-gray-700">{`${labels.benefitLabel}\u5358\u4FA1`}</th>
                <th className="px-3 py-2 text-right font-medium text-gray-700">{`${labels.benefitLabel}(\u8A08\u7B97)`}</th>
                <th className="px-3 py-2 text-right font-medium text-gray-700 bg-amber-50/50">{`${labels.benefitLabel}(\u652F\u7D66)`}</th>
              </>
            ) : (
              <>
                <th className="px-3 py-2 text-right font-medium text-gray-700">{`${labels.benefitLabel}\u5358\u4FA1`}</th>
                <th className="px-3 py-2 text-right font-medium text-gray-700">{`${labels.benefitLabel}(\u8A08\u7B97)`}</th>
                <th className="px-3 py-2 text-right font-medium text-gray-700 bg-amber-50/50">{`${labels.benefitLabel}(\u652F\u7D66)`}</th>
                <th className="px-3 py-2 text-right font-medium text-gray-700">{`${labels.allowanceLabel}\u5358\u4FA1`}</th>
                <th className="px-3 py-2 text-right font-medium text-gray-700">{`${labels.allowanceLabel}(\u8A08\u7B97)`}</th>
                <th className="px-3 py-2 text-right font-medium text-gray-700 bg-amber-50/50">{`${labels.allowanceLabel}(\u652F\u7D66)`}</th>
              </>
            )}
          </tr>
        </thead>
        <tbody>
          {payrollData.map(p => {
            const emp = findEmployee(p.employeeId);
            if (!emp) return null;
            const hours = p.totalWorkMinutes / 60;
            const { specialBenefitUnitPrice: benefitUnit, specialAllowanceUnitPrice: allowanceUnit } =
              resolveParttimeBenefitUnitPrices(emp, { benefitRuleEnabled });
            const calcBenefit = Math.round(hours * benefitUnit);
            const calcAllowance = Math.round(hours * allowanceUnit);

            return (
              <tr key={p.employeeId} className="border-b border-gray-100 hover:bg-gray-50">
                <td className="px-3 py-2 whitespace-nowrap sticky left-0 bg-white z-10">{p.employeeName}</td>
                <td className="px-3 py-2 text-right">{minutesToDecimalHours(p.totalWorkMinutes)}</td>
                {benefitRuleEnabled ? (
                  <>
                    <td className="px-3 py-2 text-right">{allowanceUnit}</td>
                    <td className="px-3 py-2 text-right">{calcAllowance.toLocaleString()}</td>
                    <td className="px-1 py-1">
                      <PayrollOverrideCell
                        value={p.specialAllowance}
                        autoValue={getAutoValue(p.employeeId, 'special_allowance')}
                        isOverridden={isOverridden(p.employeeId, 'special_allowance')}
                        format="currency"
                        onChange={(v) => onOverrideChange(p.employeeId, 'special_allowance', v)}
                      />
                    </td>
                    <td className="px-3 py-2 text-right">{benefitUnit}</td>
                    <td className="px-3 py-2 text-right">{calcBenefit.toLocaleString()}</td>
                    <td className="px-1 py-1">
                      <PayrollOverrideCell
                        value={p.specialBenefit}
                        autoValue={getAutoValue(p.employeeId, 'special_benefit')}
                        isOverridden={isOverridden(p.employeeId, 'special_benefit')}
                        format="currency"
                        onChange={(v) => onOverrideChange(p.employeeId, 'special_benefit', v)}
                      />
                    </td>
                  </>
                ) : (
                  <>
                    <td className="px-3 py-2 text-right">{benefitUnit}</td>
                    <td className="px-3 py-2 text-right">{calcBenefit.toLocaleString()}</td>
                    <td className="px-1 py-1">
                      <PayrollOverrideCell
                        value={p.specialBenefit}
                        autoValue={getAutoValue(p.employeeId, 'special_benefit')}
                        isOverridden={isOverridden(p.employeeId, 'special_benefit')}
                        format="currency"
                        onChange={(v) => onOverrideChange(p.employeeId, 'special_benefit', v)}
                      />
                    </td>
                    <td className="px-3 py-2 text-right">{allowanceUnit}</td>
                    <td className="px-3 py-2 text-right">{calcAllowance.toLocaleString()}</td>
                    <td className="px-1 py-1">
                      <PayrollOverrideCell
                        value={p.specialAllowance}
                        autoValue={getAutoValue(p.employeeId, 'special_allowance')}
                        isOverridden={isOverridden(p.employeeId, 'special_allowance')}
                        format="currency"
                        onChange={(v) => onOverrideChange(p.employeeId, 'special_allowance', v)}
                      />
                    </td>
                  </>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
